/**
 * API Service
 * Handles all API calls to the admin worker backend
 */

const API_URL = import.meta.env.VITE_WORKER_URL || 'http://localhost:8788';

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  try {
    // Get Clerk token
    const token = await window.Clerk.session.getToken();

    // Add cache-busting timestamp
    const separator = endpoint.includes('?') ? '&' : '?';
    const cacheBustedEndpoint = `${endpoint}${separator}_t=${Date.now()}`;

    const response = await fetch(`${API_URL}${cacheBustedEndpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

// Users API
export const usersApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/api/admin/users?${query}`);
  },

  get: (userId) => {
    return apiRequest(`/api/admin/users/${userId}`);
  },

  upgrade: (userId, tier, reason) => {
    return apiRequest(`/api/admin/users/${userId}/upgrade`, {
      method: 'POST',
      body: JSON.stringify({ tier, reason })
    });
  },

  syncStripe: (userId) => {
    return apiRequest(`/api/admin/users/${userId}/sync-stripe`, {
      method: 'POST'
    });
  }
};

// Analytics API
export const analyticsApi = {
  overview: () => {
    return apiRequest('/api/admin/analytics/overview');
  },

  user: (userId) => {
    return apiRequest(`/api/admin/analytics/users/${userId}`);
  },

  // Enhanced subscription analytics
  subscriptions: () => {
    return apiRequest('/api/admin/analytics/subscriptions');
  },

  revenue: () => {
    return apiRequest('/api/admin/analytics/revenue');
  },

  subscriptionTimeline: (days = 30) => {
    return apiRequest(`/api/admin/analytics/subscription-timeline?days=${days}`);
  },

  churn: (days = 30) => {
    return apiRequest(`/api/admin/analytics/churn?days=${days}`);
  },

  // User behavior analytics
  entryPatterns: (days = 30) => {
    return apiRequest(`/api/admin/analytics/entry-patterns?days=${days}`);
  },

  activityFrequency: (days = 30) => {
    return apiRequest(`/api/admin/analytics/activity-frequency?days=${days}`);
  },

  appLinks: (days = 30) => {
    return apiRequest(`/api/admin/analytics/app-links?days=${days}`);
  },

  dailyPattern: (days = 30) => {
    return apiRequest(`/api/admin/analytics/daily-pattern?days=${days}`);
  },

  retention: () => {
    return apiRequest('/api/admin/analytics/retention');
  },

  // Recent activity feed
  activity: (limit = 10) => {
    return apiRequest(`/api/admin/analytics/activity?limit=${limit}`);
  }
};

// Errors API
export const errorsApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/api/admin/errors?${query}`);
  },

  ingest: (errorData) => {
    return apiRequest('/api/admin/errors/ingest', {
      method: 'POST',
      body: JSON.stringify(errorData)
    });
  }
};

// Database API
export const databaseApi = {
  stats: () => {
    return apiRequest('/api/admin/database/stats');
  },

  userStorage: (userId) => {
    return apiRequest(`/api/admin/database/users/${userId}/storage`);
  }
};

// Medical Analytics API
export const medicalApi = {
  cohortPatterns: (filters) => {
    return apiRequest('/api/admin/medical/cohort-patterns', {
      method: 'POST',
      body: JSON.stringify(filters)
    });
  },

  anomalyDetection: (filters) => {
    return apiRequest('/api/admin/medical/anomaly-detection', {
      method: 'POST',
      body: JSON.stringify(filters)
    });
  },

  compareCohorts: (cohortA, cohortB, days = 90) => {
    return apiRequest('/api/admin/medical/compare-cohorts', {
      method: 'POST',
      body: JSON.stringify({ cohort_a: cohortA, cohort_b: cohortB, days })
    });
  },

  storeMetadata: (metadata) => {
    return apiRequest('/api/admin/medical/metadata', {
      method: 'POST',
      body: JSON.stringify(metadata)
    });
  },

  phiAudit: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/api/admin/medical/phi-audit?${query}`);
  },

  retentionReport: () => {
    return apiRequest('/api/admin/medical/retention-report');
  },

  purgeExpired: (dryRun = true) => {
    return apiRequest('/api/admin/medical/purge-expired', {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun })
    });
  }
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await fetch(`${API_URL}/health`);
    return await response.json();
  } catch (error) {
    console.error('Health check failed:', error);
    return { status: 'error' };
  }
};
